// Token Creator Component - Modal dialog for creating/editing tokens
import { EventManagerMixin } from '../utils/event-manager.js';
import { CONFIG } from '../config.js';
import { HexEvents, createHexEvent } from '../utils/hex-events.js';
import { getTokenInitials, escapeHtml } from '../utils/ui-helpers.js';
import { showFieldError, clearErrorOnInput } from '../utils/validation.js';

class TokenCreator extends EventManagerMixin(HTMLElement) {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.isOpen = false;
        this.selectedColor = CONFIG.COLORS.TOKEN_DEFAULT; // Default color (purple)
        this.imageDataUrl = null;
        this.isEditMode = false;
        this.editingToken = null;
        this.tokenName = '';
        this.selectedVisibility = CONFIG.VISIBILITY_SHOW;
    }

    connectedCallback() {
        super.connectedCallback?.();
        this.render();
        this.setupEventListeners();
    }

    // disconnectedCallback is handled by EventManagerMixin - cleans up all listeners

    open(token = null) {
        this.isOpen = true;

        if (token) {
            // Edit mode
            this.isEditMode = true;
            this.editingToken = token;
            this.tokenName = token.name;
            this.selectedColor = token.color || CONFIG.COLORS.TOKEN_DEFAULT;
            this.imageDataUrl = token.imageDataUrl || null;
            this.selectedVisibility = token.visibility || CONFIG.VISIBILITY_SHOW;
        } else {
            // Create mode
            this.isEditMode = false;
            this.editingToken = null;
            this.tokenName = '';
            this.selectedColor = CONFIG.COLORS.TOKEN_DEFAULT;
            this.imageDataUrl = null;
            this.selectedVisibility = CONFIG.VISIBILITY_SHOW;
        }

        // Clean up old listeners before re-rendering and setting up new ones
        this.cleanupListeners();
        this.render();
        this.setupEventListeners();

        // Focus name input
        setTimeout(() => {
            const nameInput = this.shadowRoot.querySelector('#token-name');
            nameInput?.focus();
        }, 100);
    }

    close() {
        this.isOpen = false;
        this.resetForm();
        this.cleanupListeners();
        this.render();
    }

    resetForm() {
        this.selectedColor = CONFIG.COLORS.TOKEN_DEFAULT;
        this.imageDataUrl = null;
        this.isEditMode = false;
        this.editingToken = null;
        this.tokenName = '';
        this.selectedVisibility = CONFIG.VISIBILITY_SHOW;
    }

    render() {
        if (!this.isOpen) {
            this.shadowRoot.innerHTML = '';
            return;
        }

        this.shadowRoot.innerHTML = `
            <style>
                .overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: ${CONFIG.COLORS.OVERLAY_DARKER};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: ${CONFIG.Z_INDEX_MODAL};
                }

                .dialog {
                    background: var(--bg-surface, #1a1a1a);
                    border: 1px solid #444;
                    border-radius: 0.5rem;
                    padding: 1.5rem;
                    width: 90%;
                    max-width: 500px;
                    max-height: 90vh;
                    overflow-y: auto;
                }

                h3 {
                    margin: 0 0 1.5rem 0;
                    font-size: 1.5rem;
                    color: var(--text-primary, #e5e5e5);
                }

                .form-group {
                    margin-bottom: 1.5rem;
                }

                label {
                    display: block;
                    margin-bottom: 0.5rem;
                    font-weight: 500;
                    color: var(--text-primary, #e5e5e5);
                }

                .required::after {
                    content: ' *';
                    color: var(--color-danger, #ef4444);
                }

                input[type="text"] {
                    width: 100%;
                    padding: 0.75rem;
                    background: var(--bg-elevated, #2a2a2a);
                    border: 1px solid #444;
                    border-radius: 0.375rem;
                    color: var(--text-primary, #e5e5e5);
                    font-size: 1rem;
                    box-sizing: border-box;
                }

                input[type="text"]:focus {
                    outline: none;
                    border-color: var(--color-primary, #3b82f6);
                }

                .char-count {
                    display: block;
                    margin-top: 0.25rem;
                    font-size: 0.875rem;
                    color: var(--text-secondary, #a0a0a0);
                    text-align: right;
                }

                .color-presets {
                    display: grid;
                    grid-template-columns: repeat(8, 1fr);
                    gap: 0.5rem;
                    margin-bottom: 0.75rem;
                }

                .color-preset {
                    width: 100%;
                    aspect-ratio: 1;
                    border: 3px solid transparent;
                    border-radius: 0.375rem;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .color-preset:hover {
                    transform: scale(1.1);
                }

                .color-preset.selected {
                    border-color: white;
                    box-shadow: 0 0 0 2px var(--bg-surface, #1a1a1a);
                }

                .color-custom {
                    width: 100%;
                    height: 40px;
                    border: 1px solid #444;
                    border-radius: 0.375rem;
                    cursor: pointer;
                }

                .visibility-selector {
                    display: flex;
                    gap: 1rem;
                }

                .visibility-selector label {
                    flex: 1;
                    margin: 0;
                    cursor: pointer;
                }

                .visibility-option {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem;
                    background: var(--bg-elevated, #2a2a2a);
                    border: 2px solid #444;
                    border-radius: 0.375rem;
                    transition: all 0.2s;
                }

                .visibility-option:hover {
                    border-color: #666;
                }

                .visibility-option.selected {
                    border-color: var(--color-primary, #3b82f6);
                    background: rgba(59, 130, 246, 0.1);
                }

                input[type="radio"] {
                    margin: 0;
                }

                .badge {
                    padding: 0.25rem 0.5rem;
                    border-radius: 0.25rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                .badge-show {
                    background: var(--color-success, #10b981);
                    color: white;
                }

                .badge-secret {
                    background: var(--color-secondary, #666);
                    color: white;
                }

                .preview {
                    margin-bottom: 1.5rem;
                    padding: 1rem;
                    background: var(--bg-elevated, #2a2a2a);
                    border: 1px solid #444;
                    border-radius: 0.375rem;
                }

                .preview h4 {
                    margin: 0 0 0.75rem 0;
                    font-size: 0.875rem;
                    color: var(--text-secondary, #a0a0a0);
                }

                .token-preview {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .token-avatar {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;
                    font-size: 1.5rem;
                    color: white;
                    flex-shrink: 0;
                }

                .token-info {
                    flex: 1;
                }

                .token-name-preview {
                    font-weight: bold;
                    margin-bottom: 0.25rem;
                    color: var(--text-primary, #e5e5e5);
                }

                .token-status-preview {
                    font-size: 0.875rem;
                    color: var(--text-secondary, #a0a0a0);
                }

                .dialog-actions {
                    display: flex;
                    gap: 0.75rem;
                    justify-content: flex-end;
                    margin-top: 1.5rem;
                }

                button {
                    padding: 0.75rem 1.5rem;
                    border: none;
                    border-radius: 0.375rem;
                    font-size: 1rem;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .btn-cancel {
                    background: var(--bg-elevated, #2a2a2a);
                    color: var(--text-primary, #e5e5e5);
                    border: 1px solid #444;
                }

                .btn-cancel:hover {
                    background: #333;
                }

                .btn-create {
                    background: var(--color-primary, #3b82f6);
                    color: white;
                }

                .btn-create:hover {
                    opacity: 0.9;
                }

                .btn-create:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .help-text {
                    font-size: 0.875rem;
                    color: var(--text-secondary, #a0a0a0);
                    margin-top: 0.25rem;
                }

                .image-upload {
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }

                input[type="file"] {
                    display: none;
                }

                .btn-upload {
                    background: var(--bg-elevated, #2a2a2a);
                    color: var(--text-primary, #e5e5e5);
                    border: 1px solid #444;
                    padding: 0.75rem;
                }

                .btn-upload:hover {
                    background: #333;
                }

                .image-preview {
                    width: 128px;
                    height: 128px;
                    border: 2px dashed #444;
                    border-radius: 0.375rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                }

                .image-preview img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .image-preview.empty {
                    color: var(--text-secondary, #a0a0a0);
                    font-size: 0.875rem;
                }
            </style>

            <div class="overlay">
                <div class="dialog">
                    <h3>${this.isEditMode ? 'Edit Token' : 'Create Token'}</h3>

                    <div class="form-group">
                        <label for="token-name" class="required">Name</label>
                        <input
                            type="text"
                            id="token-name"
                            maxlength="50"
                            placeholder="Enter token name..."
                            value="${escapeHtml(this.tokenName)}"
                            required
                        />
                        <span class="char-count">${this.tokenName.length} / 50</span>
                    </div>

                    <div class="form-group">
                        <label>Avatar Image (optional)</label>
                        <div class="image-upload">
                            <input type="file" id="token-image" accept="image/png,image/jpeg" />
                            <button type="button" class="btn-upload" id="choose-image-btn">Choose Image</button>
                            <div class="image-preview ${this.imageDataUrl ? '' : 'empty'}" id="image-preview">
                                ${this.imageDataUrl ? `<img src="${this.imageDataUrl}" alt="Token preview" />` : 'No image selected'}
                            </div>
                        </div>
                        <p class="help-text">Max 2MB, will be resized to 128x128px</p>
                    </div>

                    <div class="form-group">
                        <label>Color</label>
                        <div class="color-presets">
                            ${CONFIG.TOKEN_COLOR_PRESETS.map(color =>
                                `<button type="button" class="color-preset ${this.selectedColor === color ? 'selected' : ''}" data-color="${color}" style="background: ${color};"></button>`
                            ).join('')}
                        </div>
                        <input type="color" class="color-custom" id="color-custom" value="${this.selectedColor}" />
                    </div>

                    <div class="form-group">
                        <label>Visibility</label>
                        <div class="visibility-selector">
                            <label>
                                <div class="visibility-option ${this.selectedVisibility === CONFIG.VISIBILITY_SHOW ? 'selected' : ''}" data-visibility="${CONFIG.VISIBILITY_SHOW}">
                                    <input type="radio" name="visibility" value="${CONFIG.VISIBILITY_SHOW}" ${this.selectedVisibility === CONFIG.VISIBILITY_SHOW ? 'checked' : ''} />
                                    <span class="badge badge-show">Show to Players</span>
                                </div>
                            </label>
                            <label>
                                <div class="visibility-option ${this.selectedVisibility === CONFIG.VISIBILITY_SECRET ? 'selected' : ''}" data-visibility="${CONFIG.VISIBILITY_SECRET}">
                                    <input type="radio" name="visibility" value="${CONFIG.VISIBILITY_SECRET}" ${this.selectedVisibility === CONFIG.VISIBILITY_SECRET ? 'checked' : ''} />
                                    <span class="badge badge-secret">Secret (GM Only)</span>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div class="preview">
                        <h4>Preview</h4>
                        <div class="token-preview">
                            <div class="token-avatar" id="preview-avatar" style="background: ${this.selectedColor};">
                                ${this.imageDataUrl ? `<img src="${this.imageDataUrl}" style="width: 100%; height: 100%; border-radius: 50%;" alt="Token" />` : '?'}
                            </div>
                            <div class="token-info">
                                <div class="token-name-preview" id="preview-name">New Token</div>
                                <div class="token-status-preview">Not placed</div>
                            </div>
                        </div>
                    </div>

                    <div class="dialog-actions">
                        <button type="button" class="btn-cancel" id="cancel-btn">Cancel</button>
                        <button type="button" class="btn-create" id="create-btn" ${this.tokenName.trim().length === 0 ? 'disabled' : ''}>${this.isEditMode ? 'Save Changes' : 'Create Token'}</button>
                    </div>
                </div>
            </div>
        `;
    }

    setupEventListeners() {
        if (!this.isOpen) return;

        const nameInput = this.shadowRoot.querySelector('#token-name');
        const charCount = this.shadowRoot.querySelector('.char-count');
        const createBtn = this.shadowRoot.querySelector('#create-btn');
        const cancelBtn = this.shadowRoot.querySelector('#cancel-btn');
        const colorPresets = this.shadowRoot.querySelectorAll('.color-preset');
        const colorCustom = this.shadowRoot.querySelector('#color-custom');
        const visibilityOptions = this.shadowRoot.querySelectorAll('.visibility-option');
        const chooseImageBtn = this.shadowRoot.querySelector('#choose-image-btn');
        const imageInput = this.shadowRoot.querySelector('#token-image');
        const overlay = this.shadowRoot.querySelector('.overlay');

        // Use EventManagerMixin's addManagedListener for automatic cleanup

        // Name input validation
        if (nameInput) {
            this.addManagedListener(nameInput, 'input', () => {
                const value = nameInput.value.trim();
                const length = nameInput.value.length;
                charCount.textContent = `${length} / 50`;
                createBtn.disabled = value.length === 0;
                this.updatePreview();
            });
        }

        // Color preset selection
        colorPresets.forEach(preset => {
            this.addManagedListener(preset, 'click', () => {
                this.selectedColor = preset.dataset.color;
                colorCustom.value = this.selectedColor;
                this.cleanupListeners();
                this.render();
                this.setupEventListeners();
            });
        });

        // Custom color picker
        if (colorCustom) {
            this.addManagedListener(colorCustom, 'input', (e) => {
                this.selectedColor = e.target.value;
                this.cleanupListeners();
                this.render();
                this.setupEventListeners();
            });
        }

        // Visibility selection
        visibilityOptions.forEach(option => {
            this.addManagedListener(option, 'click', () => {
                const radio = option.querySelector('input[type="radio"]');
                radio.checked = true;
                visibilityOptions.forEach(opt => opt.classList.remove('selected'));
                option.classList.add('selected');
            });
        });

        // Image upload
        if (chooseImageBtn) {
            this.addManagedListener(chooseImageBtn, 'click', () => {
                imageInput.click();
            });
        }

        if (imageInput) {
            this.addManagedListener(imageInput, 'change', (e) => {
                const file = e.target.files[0];
                if (file) {
                    this.handleImageUpload(file);
                }
            });
        }

        // Create button
        if (createBtn) {
            this.addManagedListener(createBtn, 'click', () => {
                this.handleCreate();
            });
        }

        // Cancel button
        if (cancelBtn) {
            this.addManagedListener(cancelBtn, 'click', () => {
                this.close();
            });
        }

        // Close on overlay click
        if (overlay) {
            this.addManagedListener(overlay, 'click', (e) => {
                if (e.target === overlay) {
                    this.close();
                }
            });
        }
    }

    async handleImageUpload(file) {
        const imageInput = this.shadowRoot.querySelector('#token-image');

        // Validate file size
        if (file.size > 2 * 1024 * 1024) {
            showFieldError(imageInput, 'Image must be under 2MB');
            clearErrorOnInput(imageInput);
            return;
        }

        // Validate file type
        if (!['image/png', 'image/jpeg'].includes(file.type)) {
            showFieldError(imageInput, 'Only PNG and JPEG images supported');
            clearErrorOnInput(imageInput);
            return;
        }

        // Read and resize image
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                // Resize to 128x128
                const canvas = document.createElement('canvas');
                canvas.width = 128;
                canvas.height = 128;
                const ctx = canvas.getContext('2d');

                // Draw image (crop to square if needed)
                const size = Math.min(img.width, img.height);
                const x = (img.width - size) / 2;
                const y = (img.height - size) / 2;
                ctx.drawImage(img, x, y, size, size, 0, 0, 128, 128);

                // Convert to data URL
                this.imageDataUrl = canvas.toDataURL('image/png');
                this.cleanupListeners();
                this.render();
                this.setupEventListeners();
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    }

    updatePreview() {
        const nameInput = this.shadowRoot.querySelector('#token-name');
        const previewName = this.shadowRoot.querySelector('#preview-name');
        const previewAvatar = this.shadowRoot.querySelector('#preview-avatar');

        if (nameInput && previewName) {
            const name = nameInput.value.trim() || 'New Token';
            previewName.textContent = name;

            // Update initials if no image
            if (!this.imageDataUrl && previewAvatar) {
                const initials = getTokenInitials(name);
                previewAvatar.textContent = initials;
            }
        }
    }

    handleCreate() {
        const nameInput = this.shadowRoot.querySelector('#token-name');
        const visibilityRadio = this.shadowRoot.querySelector('input[name="visibility"]:checked');

        const name = nameInput.value.trim();
        if (!name) {
            showFieldError(nameInput, 'Please enter a token name');
            clearErrorOnInput(nameInput);
            return;
        }

        const tokenData = {
            name,
            color: this.selectedColor,
            visibility: visibilityRadio?.value || CONFIG.VISIBILITY_SHOW,
            imageDataUrl: this.imageDataUrl,
            createdAt: Date.now()
        };

        if (this.isEditMode) {
            // Dispatch edit event with token ID and updated data
            this.dispatchEvent(createHexEvent(HexEvents.TOKEN_EDITED, {
                tokenId: this.editingToken.id,
                tokenData
            }));
        } else {
            // Dispatch create event with token data
            this.dispatchEvent(createHexEvent(HexEvents.TOKEN_CREATED, tokenData));
        }

        this.close();
    }
}

customElements.define('token-creator', TokenCreator);

export default TokenCreator;
